import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#initOfferingTabs();
    this.#syncOfferingShippingNote();

    const sellingPlanId = this.querySelector("input[type='radio']:checked")?.dataset.sellingplanId;
    const variantId = this.querySelector("input[type='radio']:checked")?.dataset.variantId;

    // Check if we have a selling plan ID and if it's not already in the URL
    if (sellingPlanId) {
        const currentUrl = new URL(window.location.href);
        const currentSellingPlan = currentUrl.searchParams.get('selling_plan');

        
        // Only redirect if the parameter doesn't exist or is different
        if (!currentSellingPlan || currentSellingPlan !== sellingPlanId) {
            currentUrl.searchParams.set('selling_plan', sellingPlanId);
            currentUrl.searchParams.set('variant', variantId)
            window.location.href = currentUrl.toString();
        }
    }
  }

  /**
   * Subscribe & save / One-time purchase tabs on the USA offering picker.
   * Capture-phase so the "Subscribe & save up to 44%" chip always switches tabs
   * before a parent label can select the one-time radio.
   */
  #initOfferingTabs() {
    if (this.dataset.offeringTabsBound === 'true') return;
    this.dataset.offeringTabsBound = 'true';

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const switchChip = target.closest('[data-switch-to-subscribe]');
        if (switchChip instanceof HTMLElement && this.contains(switchChip)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.#activateOfferingPanel('subscribe', switchChip.dataset.targetVariantId);
          return;
        }

        const tab = target.closest('[data-offering-tabs] [role="tab"][data-panel]');
        if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;

        event.preventDefault();
        this.#activateOfferingPanel(tab.dataset.panel);
      },
      true
    );
  }

  /**
   * @param {string | undefined} panelId
   * @param {string | undefined} [preferredVariantId]
   */
  #activateOfferingPanel(panelId, preferredVariantId) {
    if (!panelId) return;

    const tablist = this.querySelector('[data-offering-tabs]');
    if (!(tablist instanceof HTMLElement)) return;

    const tabs = tablist.querySelectorAll('[role="tab"][data-panel]');
    for (const t of tabs) {
      if (!(t instanceof HTMLElement)) continue;
      const selected = t.dataset.panel === panelId;
      t.classList.toggle('offering-tab--active', selected);
      t.setAttribute('aria-selected', selected ? 'true' : 'false');
    }

    const panels = this.querySelectorAll('[data-offering-panel]');
    let activePanel = null;
    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      const isActive = panel.dataset.offeringPanel === panelId;
      panel.hidden = !isActive;
      if (isActive) activePanel = panel;
    }

    if (!(activePanel instanceof HTMLElement)) return;

    /** @type {HTMLInputElement | null} */
    let radio = null;
    const panelRadios = Array.from(activePanel.querySelectorAll('input[type="radio"]')).filter(
      (input) => input instanceof HTMLInputElement
    );

    if (preferredVariantId) {
      radio =
        panelRadios.find((input) => input.dataset.variantId === preferredVariantId) ??
        null;
    }
    if (!radio) {
      radio = this.#quarterlyOfferingRadio(panelRadios);
    }
    if (!radio) return;

    radio.checked = true;
    this.updateSelectedOption(radio);
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Default option inside a tab is the 3-month supply. Falls back to the last radio.
   * @param {HTMLInputElement[]} radios
   * @returns {HTMLInputElement | null}
   */
  #quarterlyOfferingRadio(radios) {
    const quarterly = radios.find((input) => {
      const label = `${input.value} ${input.getAttribute('aria-label') || ''}`.toLowerCase();
      return label.includes('3-month') || label.includes('3 month') || label.includes('3-pack') || label.includes('3 pack');
    });

    return quarterly ?? radios[radios.length - 1] ?? null;
  }

  /**
   * Swaps the shipping note under the offering picker.
   * Subscribe keeps the free-shipping line; One-time purchase shows the $5.95 line.
   */
  #syncOfferingShippingNote() {
    const checked = this.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) return;

    const panel = checked.dataset.sellingplanId ? 'subscribe' : 'one-time';
    const section = this.closest('.shopify-section') ?? document;
    const note = section.querySelector('[data-offering-shipping-note]');
    if (!(note instanceof HTMLElement)) return;

    for (const line of note.querySelectorAll('[data-offering-note]')) {
      if (!(line instanceof HTMLElement)) continue;
      line.hidden = line.dataset.offeringNote !== panel;
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? selectedOption.dataset.variantId }, { id: selectedOption.dataset.sellingPlanId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const sellingPlan = selectedOption.dataset.sellingplanId || null;

    this.#syncOfferingShippingNote();
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption, null, [], sellingPlan), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;
   

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }

      if(sellingPlan){
        url.searchParams.set('selling_plan', sellingPlan);
      } else {
        url.searchParams.delete('selling_plan');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], sellingPlan = null) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if(this.selectedVariantIds.length && !source){
       params.push(`variant=${this.selectedVariantIds.join(',')}`);
    }

    if(sellingPlan){
      params.push(`selling_plan=${sellingPlan}`)
    }
    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        const pickerEl = html.querySelector('variant-picker');
        let sellingPlanId = '';

        if (pickerEl) {
          const radio = pickerEl.querySelector('input[type="radio"][data-current-checked="true"]');
          if (radio instanceof HTMLInputElement) {
            sellingPlanId = radio.dataset.sellingplanId ?? '';
          } else {
            const option = pickerEl.querySelector('select.variant-option__select option:checked');
            if (option instanceof HTMLOptionElement) {
              sellingPlanId = option.getAttribute('data-sellingplan-id') || '';
            }
          }
        }


        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId || this.selectedVariantIds.length) {
            let productData;
            try {
              productData = JSON.parse(textContent);
            } catch (e) {
              console.error('Failed to parse variant JSON', e);
              return;
            }
            const sellingPlanPayload = { id: sellingPlanId || '' };
            this.dispatchEvent(
              new VariantUpdateEvent(
                productData,
                sellingPlanPayload,
                this.selectedOptionId || this.selectedVariantIds[0],
                {
                  html,
                  productId: this.dataset.productId ?? '',
                  newProduct,
                }
              )
            );
          }
        }

        if(document.querySelector("sticky-add-to-cart-component")){
          document.querySelector("sticky-add-to-cart-component").innerHTML = html.querySelector("sticky-add-to-cart-component").innerHTML
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }


  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }
    // Commented because of the Subscription Change
    //morph(this, newVariantPickerSource);
    return newProduct;

  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      //throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      //if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    }).filter(data => !!data);
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedVariantIds() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { variantId } = option.dataset;

      //if (!variantId) throw new Error('No variant value ID found');

      return variantId;
    }).filter(data => !!data);
  }

  
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
